// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useEffect, useRef } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { Outlet, useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import { useIdpInfo } from '../contexts/IdpContext';
import { useUserState } from '../contexts/UserContext';
import { useConfig } from '../hooks/other/useConfig';

export const AuthCheck: React.FC = () => {
  const { loggedInUser, login } = useUserState();
  const { idpInfo } = useIdpInfo();
  const config = useConfig();
  const auth = useAuth();

  const nav = useNavigate();

  const restoredUrlState = useRef(false);

  useEffect(() => {
    if (config.app.mode === 'utility' && idpInfo?.algorithm === 'rs-256') {
      // Don't do any redirects if the oidcAuth is still loading.
      if (auth.isLoading) {
        return;
      }

      if (!auth.isAuthenticated) {
        if (auth.error) {
          // Avoid a redirect loop on a persistent IdP error; let the user retry from the login page.
          nav('/login');
        } else if (!hasAuthParams() && !auth.activeNavigator) {
          // Tokens live in memory only, so a page reload loses the session. Redirect to the
          // IdP now; its SSO cookie restores the session silently, without a login prompt.
          // Carry the current path in url_state so it can be restored after the callback.
          void auth.signinRedirect({
            url_state: window.location.pathname + window.location.search,
          });
        }
      } else if (auth.user?.url_state && !restoredUrlState.current) {
        // The signin callback lands on redirect_uri (the app root), so navigate back to the
        // page the user was on. The ref makes this run once, since url_state stays on the
        // user object across silent renews.
        restoredUrlState.current = true;
        nav(auth.user.url_state, { replace: true });
      }

      // return early to avoid hitting the rest of the logic before the redirect completes.
      return;
    }

    if (!loggedInUser && idpInfo?.algorithm === 'rs-256') {
      login();
    } else if (!loggedInUser && idpInfo?.algorithm === 'idp-not-set') {
      nav('/login');
    } else if (!loggedInUser) {
      nav('/login');
    }
  }, [auth, config, loggedInUser, idpInfo, nav, login]);

  const waitForOauth = idpInfo?.algorithm === 'rs-256' && auth?.isLoading && !auth?.isAuthenticated;

  if (waitForOauth || !idpInfo || !loggedInUser) {
    return <Loading text="Loading user..." />;
  }

  return <Outlet />;
};
